import { Component } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { GeneralService } from './services/general.service';
import { WordReplacementService } from './services/word-replacement.service';
import { TenantService } from './services/tenant.service';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';   // ADDED
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {

  private readonly MIN_ZOOM = 1;
  private readonly MAX_ZOOM = 3;
  private zoomLevel = 1;
  private lastZoom = 1;
  private initialPinchDistance = 0;
  private isPinching = false;

  constructor(
    private generalService: GeneralService,
    private wordReplacementService: WordReplacementService,
    private tenantService: TenantService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (Capacitor.isNativePlatform()) {

      this.setupStatusBar();   // ADDED: app starts below the status bar, no overlap

      this.tenantService.config$.pipe(take(1)).subscribe(cfg => {
        if (cfg?.marketId) {
          this.loadMarketTerms(cfg.marketId);
        }
      });

      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(e => {
          if (e.urlAfterRedirects.startsWith('/ops')) {
            localStorage.setItem('lastRoute', e.urlAfterRedirects);
          }
        });

      this.initPushNotifications();
    }
  }

  // ADDED: runtime guarantee, in case the capacitor.config.ts value is not picked up.
  private async setupStatusBar(): Promise<void> {
    try {
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setBackgroundColor({ color: '#0b0b1f' });
      }
      await StatusBar.setStyle({ style: Style.Dark });
    } catch (e) {
      console.warn('StatusBar setup failed', e);
    }
  }

  private initPushNotifications(): void {
    PushNotifications.requestPermissions().then(result => {
      if (result.receive === 'granted') {
        PushNotifications.register();
      } else {
        console.warn('Push notification permission denied');
      }
    });

    // Called when registration succeeds — gives you the device token
    PushNotifications.addListener('registration', token => {
      console.log('Push registration success, token: ' + token.value);
      // TODO: send this token to your backend so you can target this device
      // this.generalService.saveDeviceToken(token.value).subscribe();
    });

    // Called if registration fails
    PushNotifications.addListener('registrationError', error => {
      console.error('Push registration error: ', JSON.stringify(error));
    });

    // Called when a push notification is received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', notification => {
      console.log('Push received (foreground): ', notification);
    });

    // Called when user taps a notification (app in background/killed)
    PushNotifications.addListener('pushNotificationActionPerformed', action => {
      console.log('Push action performed: ', action);
      const data = action.notification.data;
      if (data?.route) {
        this.router.navigateByUrl(data.route);
      }
    });
  }

  private loadMarketTerms(marketId: string): void {
    this.generalService.getTermsUsedInMarket(marketId)
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.wordReplacementService.setTermMap({
            booth: res.BoothTerm,
            vendor: res.VendorTerm,
            market: res.MarketTerm
          });
        }
      });
  }

  // --- Pinch-to-zoom (2-finger only; 1-finger scroll/taps untouched) ---

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 2) {
      this.isPinching = true;
      this.initialPinchDistance = this.getDistance(event.touches);
      this.lastZoom = this.zoomLevel;
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isPinching || event.touches.length !== 2) {
      return;
    }
    event.preventDefault();

    const currentDistance = this.getDistance(event.touches);
    const ratio = currentDistance / (this.initialPinchDistance || currentDistance);
    let newZoom = this.lastZoom * ratio;
    newZoom = Math.min(this.MAX_ZOOM, Math.max(this.MIN_ZOOM, newZoom));

    this.zoomLevel = newZoom;
    this.applyZoom();
  }

  onTouchEnd(event: TouchEvent): void {
    if (event.touches.length < 2) {
      this.isPinching = false;
      if (this.zoomLevel <= this.MIN_ZOOM) {
        this.zoomLevel = this.MIN_ZOOM;
        this.applyZoom();
      }
    }
  }

  private applyZoom(): void {
    const el = document.getElementById('zoom-wrapper') as (HTMLElement & { style: CSSStyleDeclaration & { zoom?: string } }) | null;
    if (el) {
      // CSS `zoom` is non-standard but supported in Chromium/WebKit (Capacitor's WebViews),
      // and unlike `transform: scale()`, it does NOT create a new containing block for
      // `position: fixed` descendants — so ion-tab-bar etc. remain correctly pinned.
      (el.style as any).zoom = `${this.zoomLevel}`;
    }
  }

  private getDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}